import type {
  CommandOperationsRecord,
  CommandOperationsSummary,
  DeviceCommandType,
  Location,
  Screen,
} from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import { formatRelativeTime } from '../../lib/dashboard/relativeTime';

export type FixesTab = 'All' | 'Waiting' | 'Worked' | "Didn't work";
export type FixesPeriod = '24h' | '7d' | '30d';

export type FixOutcomeGroup = 'Waiting' | 'Worked' | "Didn't work";

export type RemoteFixRow = {
  id: string;
  label: string;
  screenName: string;
  screenId: string | null;
  locationName: string;
  statusLabel: string;
  statusColor: string;
  note: string;
  issuedBy: string;
  when: string;
  canRetry: boolean;
  deviceId: string;
  type: CommandOperationsRecord['type'];
};

const TYPE_LABEL_KEY = {
  RESTART_PLAYER: 'restart',
  SYNC_NOW: 'syncNow',
  CLEAR_CACHE: 'clearCache',
  REFRESH_CONTENT: 'refreshContent',
  REQUEST_SCREENSHOT: 'screenshot',
  COLLECT_LOGS: 'collectLogs',
} as const satisfies Record<DeviceCommandType, string>;

export const FIXES_PERIODS: readonly { id: FixesPeriod; days: number }[] = [
  { id: '24h', days: 1 },
  { id: '7d', days: 7 },
  { id: '30d', days: 30 },
] as const;

export const FIXES_TABS: readonly FixesTab[] = [
  'All',
  'Waiting',
  'Worked',
  "Didn't work",
] as const;

export function outcomeGroup(status: CommandOperationsRecord['status']): FixOutcomeGroup {
  if (status === 'executed') return 'Worked';
  if (status === 'failed' || status === 'expired') return "Didn't work";
  return 'Waiting';
}

export function fixTypeLabel(type: DeviceCommandType): string {
  return i18n.t(`operations:fixTypes.${TYPE_LABEL_KEY[type]}`);
}

export function buildTabCounts(summary: CommandOperationsSummary | undefined): Record<FixesTab, number> {
  if (!summary) {
    return { All: 0, Waiting: 0, Worked: 0, "Didn't work": 0 };
  }
  const waiting = summary.pending + summary.sent + summary.acknowledged;
  const bad = summary.failed + summary.expired;
  return {
    All: summary.totalCreated,
    Waiting: waiting,
    Worked: summary.executed,
    "Didn't work": bad,
  };
}

export function buildFixesHero(summary: CommandOperationsSummary | undefined): {
  title: string;
  subtitle: string;
  color: string;
} {
  if (!summary) {
    return {
      title: i18n.t('operations:hero.defaultTitle'),
      subtitle: i18n.t('operations:hero.defaultSubtitle'),
      color: 'var(--gs-muted)',
    };
  }

  const waiting = summary.pending + summary.sent + summary.acknowledged;
  const bad = summary.failed + summary.expired;

  if (waiting > 0) {
    return {
      title: i18n.t('operations:hero.waitingTitle', { count: waiting }),
      subtitle: i18n.t('operations:hero.waitingSubtitle', { count: waiting }),
      color: 'var(--gs-warn)',
    };
  }

  if (bad > 0) {
    return {
      title: i18n.t('operations:hero.failedTitle'),
      subtitle: i18n.t('operations:hero.failedSubtitle'),
      color: 'var(--gs-bad)',
    };
  }

  return {
    title: i18n.t('operations:hero.okTitle'),
    subtitle: i18n.t('operations:hero.okSubtitle'),
    color: 'var(--gs-ok)',
  };
}

export function emptyFixesCopy(tab: FixesTab): string {
  switch (tab) {
    case 'Waiting':
      return i18n.t('operations:empty.waiting');
    case "Didn't work":
      return i18n.t('operations:empty.didntWork');
    default:
      return i18n.t('operations:empty.default');
  }
}

export function buildRemoteFixRow(
  item: CommandOperationsRecord,
  screens: Screen[],
  locations: Location[],
): RemoteFixRow {
  const screen = screens.find((s) => s.deviceId === item.deviceId) ?? null;
  const location = screen?.locationId
    ? (locations.find((l) => l.id === screen.locationId) ?? null)
    : null;
  const group = outcomeGroup(item.status);

  let statusLabel = i18n.t('operations:status.waitingForScreen');
  if (item.status === 'acknowledged' || (item.status === 'sent' && !item.isLongWaiting)) {
    statusLabel = i18n.t('operations:status.working');
  }
  if (item.status === 'pending' || (item.status === 'sent' && item.isLongWaiting)) {
    statusLabel = i18n.t('operations:status.waitingForScreen');
  }
  if (item.status === 'executed') statusLabel = i18n.t('operations:status.worked');
  if (item.status === 'failed') statusLabel = i18n.t('operations:status.didntWork');
  if (item.status === 'expired') statusLabel = i18n.t('operations:status.timedOut');

  let note = '';
  if (group === 'Waiting') {
    note =
      item.status === 'pending'
        ? i18n.t('operations:notes.offline')
        : item.isLongWaiting
          ? i18n.t('operations:notes.longWaiting')
          : i18n.t('operations:notes.sent');
  } else if (item.status === 'failed') {
    note = item.resultMessage ?? i18n.t('operations:notes.noResponse');
  } else if (item.status === 'expired') {
    note = i18n.t('operations:notes.expired');
  } else if (item.status === 'executed') {
    note = item.resultMessage ?? i18n.t('operations:notes.reached');
  }

  return {
    id: item.id,
    label: fixTypeLabel(item.type),
    screenName: screen?.name ?? item.deviceCode,
    screenId: screen?.id ?? null,
    locationName: location?.name ?? '',
    statusLabel,
    statusColor:
      group === 'Waiting'
        ? 'var(--gs-warn)'
        : group === 'Worked'
          ? 'var(--gs-ok)'
          : 'var(--gs-bad)',
    note,
    issuedBy: item.issuedByDisplayName ?? i18n.t('operations:unknownOperator'),
    when: formatRelativeTime(item.createdAt),
    canRetry: item.status === 'failed' || item.status === 'expired',
    deviceId: item.deviceId,
    type: item.type,
  };
}

export function filterRecordsForTab(
  items: CommandOperationsRecord[],
  tab: FixesTab,
): CommandOperationsRecord[] {
  if (tab === 'All') return items;
  return items.filter((item) => outcomeGroup(item.status) === tab);
}
